import {Button, Col, Divider, Modal, Radio, Row, Select, Space, Typography, Upload, UploadFile} from "antd";
import React, {useCallback, useMemo, useState} from "react";
import {
    useGetDocumentsQuery,
    useGetFolderLabelQuery,
    useLazyRequestToSaveDocumentsQuery,
    useUpdateDocumentMutation
} from "../../redux/api/documentsApiSlice";
import {useParams} from "react-router-dom";
import {skipToken} from "@reduxjs/toolkit/dist/query";
import {CloseCircleOutlined, UploadOutlined} from "@ant-design/icons";
import axios from "axios";
import {MinimumDocumentModel} from "../../types/negotiations";
import {useGetNegotiationDetailQuery} from "../../redux/api/negotiationsApiSlice";

export type UploadDoc = UploadFile & { dirType: string }

export default function UploadMinimumDoc({documentTypeList, callBackUpdateNegotiation, minimumDocuments}: {
    documentTypeList: { label: string, value: string }[],
    callBackUpdateNegotiation: () => void,
    minimumDocuments: MinimumDocumentModel
}) {
    const {id} = useParams()

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [uploadType, setUploadType] = useState<string>("new")
    const [selectedMediaLabel, setSelectedMediaLabel] = useState<string | null>(null)
    const [selectedFile, setSelectedFile] = useState<string | null>(null)

    const [directoryType, setDirectoryType] = useState<string | undefined>(undefined)
    const [uploadedFile, setUploadedFile] = useState<File | undefined>(undefined)
    const [docList, setDocList] = useState<(File & { name: string; type: string; uid: string; })[]>([]);
    const [filesTooLong, setFilesTooLong] = useState<string[]>([])
    const [loadingUpload, setLoadingUpload] = useState<boolean>(false)
    const maxFileSize = 50 * 1024 * 1024 // 50 MB

    const {data: documentsData} = useGetDocumentsQuery(id ? {negotiation_id: id} : skipToken)
    const {data: foldersLabelData} = useGetFolderLabelQuery()
    const [saveDocument] = useLazyRequestToSaveDocumentsQuery()
    const [updateDocument] = useUpdateDocumentMutation();
    const {data: negotiation } = useGetNegotiationDetailQuery(id ?? skipToken);

    const uploadedDocuments = useMemo(() => {
        if (selectedMediaLabel && negotiation?.negotiation_analysis?.minimum_documents && Array.isArray(negotiation?.negotiation_analysis?.minimum_documents)) {
            return negotiation?.negotiation_analysis?.minimum_documents
                .find(doc => doc.media_label_uuid === selectedMediaLabel)
                ?.documents.map(doc => doc.document_uuid);
        }

        return undefined;
    }, [negotiation?.negotiation_analysis?.minimum_documents, selectedMediaLabel]);

    const isSelectedFileAlreadyUploaded = selectedFile && uploadedDocuments?.includes(selectedFile);

    const saveInAws = useCallback((data: any, file: File) => {
        const postData = new FormData()
        for (const key in data.fields)
            postData.append(key, data.fields[key])
        postData.append('file', file)
        return axios.post(data.url, postData)
    }, [])

    const saveFiles = useCallback(async (file: File) => {
        try {
            const responseSaveDocs = await saveDocument({
                path: directoryType,
                name: file.name,
                negotiation: id,
                content_type: file.type
            }).unwrap()
            await saveInAws(responseSaveDocs, file)
            if (selectedMediaLabel) {
                await updateDocument({
                    uuid: responseSaveDocs.uuid,
                    data: {is_valid: true, name: file.name, negotiation: id, media_label_uuid: selectedMediaLabel}
                })
                setLoadingUpload(false)
                callBackUpdateNegotiation()
                closeModal()
            }
        } catch {
            console.error('saveFiles')
            return new Error('errore')
        }
    }, [callBackUpdateNegotiation, directoryType, id, saveDocument, saveInAws, selectedMediaLabel, updateDocument])

    const findAnalysisDocByUuid = useCallback(() => {
        for (const item of minimumDocuments) {
            if (item.documents && item.documents.length > 0) {
                const foundDocument = item.documents.find(doc => doc.document_uuid === selectedFile);
                if (foundDocument) {
                    return foundDocument;
                }
            }
        }
        return null;
    }, [minimumDocuments, selectedFile])

    const onUploadDocument = useCallback(() => {
        if (uploadType === "doc" && selectedFile && selectedMediaLabel) {
            const media_s3_path = findAnalysisDocByUuid()?.media_s3_path
            updateDocument({uuid: selectedFile, data: {negotiation: id, media_label_uuid: selectedMediaLabel, media_s3_path }}).unwrap()
                .then(res => {
                    callBackUpdateNegotiation()
                    closeModal()
                })
        } else if (uploadType === "new" && uploadedFile) {
            void saveFiles(uploadedFile)
        }
    }, [callBackUpdateNegotiation, findAnalysisDocByUuid, id, saveFiles, selectedFile, selectedMediaLabel, updateDocument, uploadType, uploadedFile])

    const closeModal = () => {
        setDirectoryType(undefined)
        setSelectedFile(null)
        setDocList([])
        setUploadType("new")
        setIsModalOpen(false)
        setSelectedMediaLabel(null);
    }

    const isUploadDisabled = useCallback(() => {
        if (!selectedMediaLabel) return true;

        if (uploadType === "doc") {
            return !selectedFile || !!isSelectedFileAlreadyUploaded;
        }

        if (uploadType === "new") {
            return !directoryType || docList.length === 0;
        }

        return true;
    }, [directoryType, docList.length, selectedFile, selectedMediaLabel, uploadType])

    return <>
        <Modal
            open={isModalOpen}
            title={"Carica documento"}
            destroyOnClose
            footer={false}
            onCancel={() => closeModal()}>
            <Space direction={"vertical"} style={{width: "100%"}}>
                <strong>
                    Tipologia documento
                </strong>
                <div style={{marginBottom: "1rem"}}>
                    <Select
                        placeholder={"Seleziona"}
                        value={selectedMediaLabel}
                        style={{width: "100%"}}
                        options={documentTypeList}
                        onChange={(value) => setSelectedMediaLabel(value)}
                    />
                </div>
                <strong>
                    Tipologia di caricamento
                </strong>
                <Radio.Group
                    value={uploadType}
                    onChange={e => {
                        setDirectoryType(undefined);
                        setSelectedFile(null);
                        setDocList([]);
                        setUploadType(e.target.value);
                    }}>
                    <Radio value={"new"}>
                        Carica nuovo
                    </Radio>
                    <Radio value={"doc"}>
                        Seleziona da documentale
                    </Radio>
                </Radio.Group>
                {uploadType === "doc" &&
                    <div style={{marginTop: "1rem"}}>
                        <Space direction={"vertical"} style={{width: "100%"}}>
                            <strong>
                                Seleziona documento
                            </strong>
                            <div style={{width: "100%"}}>
                                <Select
                                    value={selectedFile}
                                    status={isSelectedFileAlreadyUploaded ? "error" : undefined}
                                    placeholder={"Seleziona"}
                                    style={{width: "100%"}}
                                    options={documentsData?.map(el => ({label: el.name, value: el.uuid}))}
                                    onChange={(value) => setSelectedFile(value)}
                                />
                            </div>
                            { isSelectedFileAlreadyUploaded && <Typography.Text type="danger">Il file selezionato è già stato caricato</Typography.Text> }
                        </Space>
                    </div>}
                {uploadType === "new" && <div style={{marginTop: "1rem"}}>
                    <Space direction={"vertical"} style={{width: "100%"}}>
                        <strong>
                            Cartella di destinazione
                        </strong>
                        <Select
                            virtual={false}
                            className={'custom-select-documents'}
                            style={{width: '100%'}}
                            options={foldersLabelData}
                            value={directoryType}
                            onChange={(v) => setDirectoryType(v)}
                            placeholder={"Seleziona"}
                        />
                        <Upload
                            accept={"image/*, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-word.document.macroEnabled.12, application/zip, application/octet-stream, application/x-zip-compressed, multipart/x-zip, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel.sheet.macroEnabled.12, message/rfc822, application/vnd.ms-outlook, application/x-msg, application/octet-stream, application/acad, application/vnd.oasis.opendocument.text, application/vnd.oasis.opendocument.spreadsheet, application/pkcs7-mime, application/x-pkcs7-mime"}
                            className={"upload-doc"}
                            multiple={false}
                            onRemove={file => {
                                const index = docList.findIndex(el => el.uid === file.uid);
                                const newFileList = docList.slice();
                                newFileList.splice(index, 1);
                                setDocList(newFileList);
                            }}
                            onChange={(info) => {
                                setUploadedFile(info.fileList[0].originFileObj)
                            }}
                            beforeUpload={(file, fileList) => {
                                let _file = {
                                    ...file as object,
                                    name: file.name,
                                    type: file.type
                                } as unknown as any
                                const isBelowMaxSize = file.size <= maxFileSize;
                                if (!isBelowMaxSize) {
                                    setFilesTooLong(prevState => {
                                        return [...prevState, file.name]
                                    })
                                } else
                                    setDocList(prevState => [...prevState, _file])
                                return false
                            }}
                        >
                            {docList.length === 0 && <Button
                                style={{width: '100%', marginTop: "10px", border: "1px dashed"}}
                                icon={<UploadOutlined/>}
                            >
                                Seleziona o trascina documenti
                            </Button>}
                        </Upload>
                        {filesTooLong.length !== 0 && <div>
                            <Divider/>
                            <Row justify={"center"} style={{marginBottom: "15px"}}><h3>I seguenti documenti non verranno caricati</h3>
                            </Row>
                            {filesTooLong.map(el => {
                                return <Row justify={"space-between"}>
                                    <Col span={12}>
                                        <div className={"ellipsis-name"}>{el}</div>
                                    </Col>
                                    <Col span={12}>
                                        <div className={"ellipsis-name"}><CloseCircleOutlined style={{color: "red", marginRight: "5px"}}/>Dimensione
                                            massima superata
                                        </div>
                                    </Col>
                                </Row>
                            })}
                        </div>}
                    </Space>
                </div>
                }
                <Row justify={"end"} style={{marginTop: "1rem"}}>
                    <Button
                        type={"primary"}
                        loading={loadingUpload}
                        disabled={isUploadDisabled()}
                        onClick={() => onUploadDocument()}>
                        Carica
                    </Button>
                </Row>
            </Space>
        </Modal>
        <Button
            type="primary"
            size="small"
            onClick={() => setIsModalOpen(true)}
        >
            Carica documento
        </Button>
    </>
}